<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  colleagues: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  busy: { type: Boolean, default: false },
});
const emit = defineEmits(['transfer', 'back']);
const { t } = useI18n();
const query = ref('');
const shown = computed(() => {
  const text = query.value.trim().toLowerCase();
  if (!text) return props.colleagues;
  return props.colleagues.filter(
    person =>
      person.name.toLowerCase().includes(text) ||
      person.extension.includes(text)
  );
});
const dot = availability =>
  ({ online: 'bg-n-teal-9', busy: 'bg-n-amber-9' })[availability] ||
  'bg-n-slate-8';
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex items-center gap-2">
      <Button
        icon="i-ph-arrow-left-bold"
        variant="ghost"
        color="slate"
        size="sm"
        :aria-label="t('RELAY_VOICE.BACK')"
        @click="emit('back')"
      />
      <h3 class="text-sm font-semibold text-n-slate-12">
        {{ t('RELAY_VOICE.TRANSFER_TITLE') }}
      </h3>
    </div>
    <p class="text-xs text-n-slate-11 leading-relaxed">
      {{ t('RELAY_VOICE.TRANSFER_HINT') }}
    </p>
    <label
      class="flex items-center gap-2 rounded-lg bg-n-alpha-2 px-3 py-2 focus-within:outline focus-within:outline-2 focus-within:outline-n-brand"
    >
      <span
        class="i-ph-magnifying-glass-bold size-4 text-n-slate-10"
        aria-hidden="true"
      />
      <input
        v-model="query"
        type="search"
        :aria-label="t('RELAY_VOICE.TRANSFER_SEARCH')"
        :placeholder="t('RELAY_VOICE.TRANSFER_SEARCH')"
        class="flex-1 bg-transparent border-0 p-0 text-sm text-n-slate-12 outline-none !mb-0"
      />
    </label>
    <div v-if="loading" class="flex justify-center py-4"><Spinner /></div>
    <p
      v-else-if="!shown.length"
      class="text-sm text-n-slate-10 text-center py-4"
    >
      {{ t('RELAY_VOICE.TRANSFER_EMPTY') }}
    </p>
    <ul v-else class="flex flex-col max-h-60 overflow-y-auto -mx-1">
      <li v-for="person in shown" :key="person.user_id">
        <button
          type="button"
          :disabled="busy"
          class="w-full flex items-center gap-3 px-2 py-2 rounded-lg text-start hover:bg-n-alpha-2 disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
          :aria-label="t('RELAY_VOICE.TRANSFER_TO', { name: person.name })"
          @click="emit('transfer', person)"
        >
          <span class="relative shrink-0">
            <Avatar :src="person.avatar_url" :name="person.name" :size="32" />
            <span
              class="absolute -bottom-0.5 -end-0.5 size-2.5 rounded-full ring-2 ring-n-surface-1"
              :class="dot(person.availability)"
            />
          </span>
          <span class="flex-1 min-w-0">
            <span class="block text-sm font-medium text-n-slate-12 truncate">
              {{ person.name }}
            </span>
            <span class="block text-xs text-n-slate-10">
              {{ t('RELAY_VOICE.EXTENSION', { extension: person.extension }) }}
              · {{ t(`RELAY_VOICE.AVAILABILITY.${person.availability}`) }}
            </span>
          </span>
          <span
            class="i-ph-arrow-bend-up-right-bold size-4 text-n-slate-10"
            aria-hidden="true"
          />
        </button>
      </li>
    </ul>
  </div>
</template>
